import { useCallback, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
  type CoverageCellStateWire,
  getDefaultCoverageRange,
  isValidIsoDate,
} from "#utils/coverageGrid.utils";

/** URL query-param keys the coverage view persists its filter state under so a
 *  page refresh (or a shared link) reproduces the reviewer's picked window
 *  instead of snapping back to the current-quarter default. We stick to
 *  `history.replace` for every write — back/forward should stay unchanged so
 *  the reviewer isn't cycling through DatePicker keystrokes to leave the
 *  page. Multi-value filters (funds, statuses) are comma-joined; empty means
 *  "not filtered" and the param is dropped rather than left as `?funds=`.
 *
 *  This module is the only router-coupled part of `CoverageView` — the
 *  component itself stays free of `react-router-dom`, mirroring how
 *  `usePackageRoute` holds the package-detail deep-link coupling. */
const START_PARAM = "start";
const END_PARAM = "end";
const FUNDS_PARAM = "funds";
const STATUSES_PARAM = "statuses";

type DateRange = { start: string; end: string };

// The DS Select's `onChange` value type is `(string | number)[]` — narrow to
// `string` here so callers (URL parser, Select onChange filter) share one
// guard that also rules out numeric option values.
export const isString = (v: unknown): v is string => typeof v === "string";

/** A status outside the flag-aware valid set (a stale URL param, or
 *  CONFIRMED_ISSUES while `fayMVPAugust` is off) is dropped, not leaked. */
export const isValidStatus =
  (validStatuses: ReadonlySet<CoverageCellStateWire>) =>
  (v: unknown): v is CoverageCellStateWire =>
    isString(v) && validStatuses.has(v);

export type UrlFilters = {
  dateRange: DateRange;
  funds: string[];
  statuses: CoverageCellStateWire[];
};

/** Merge a URL `search` string into a full `UrlFilters` object, falling back to
 *  the default range / empty multi-select when a param is missing or invalid.
 *  Read once on mount; further writes use `writeFiltersToSearch`. `validStatuses`
 *  is threaded in (not module-level) because it depends on the `fayMVPAugust`
 *  flag. */
function readFiltersFromSearch(
  search: string,
  validStatuses: ReadonlySet<CoverageCellStateWire>,
): UrlFilters {
  const params = new URLSearchParams(search);
  const start = params.get(START_PARAM);
  const end = params.get(END_PARAM);
  const dateRange =
    start && end && isValidIsoDate(start) && isValidIsoDate(end) && start <= end
      ? { start, end }
      : getDefaultCoverageRange();
  const fundsParam = params.get(FUNDS_PARAM);
  const funds = fundsParam ? fundsParam.split(",").filter(Boolean) : [];
  const statusesParam = params.get(STATUSES_PARAM);
  const statuses = statusesParam
    ? statusesParam.split(",").filter(isValidStatus(validStatuses))
    : [];
  return { dateRange, funds, statuses };
}

/** Encode the given `UrlFilters` back into a query string, preserving any other
 *  params already on the URL. A multi-select with zero selections drops its
 *  param entirely (so a shared URL doesn't carry a stale `?funds=`). */
function writeFiltersToSearch(
  currentSearch: string,
  { dateRange, funds, statuses }: UrlFilters,
): string {
  const params = new URLSearchParams(currentSearch);
  params.set(START_PARAM, dateRange.start);
  params.set(END_PARAM, dateRange.end);
  if (funds.length > 0) {
    params.set(FUNDS_PARAM, funds.join(","));
  } else {
    params.delete(FUNDS_PARAM);
  }
  if (statuses.length > 0) {
    params.set(STATUSES_PARAM, statuses.join(","));
  } else {
    params.delete(STATUSES_PARAM);
  }
  return params.toString();
}

export type UseUrlFiltersReturn = {
  filters: UrlFilters;
  /** Functional update; every write mirrors the next filters into the URL via
   *  `history.replace`, preserving any other params already on it. */
  setFilters: (updater: (prev: UrlFilters) => UrlFilters) => void;
};

/** Seeds the filters from the URL once on mount (lazy `useState` initializer —
 *  persists across refresh and makes shareable links reproduce the reviewer's
 *  picked window), falling back to the current-quarter default range and empty
 *  multi-selects. Subsequent edits flow through `setFilters`, which uses
 *  `history.replace` so the browser back button stays "back to the previous
 *  page" instead of cycling through DatePicker keystrokes. Seeded from
 *  `validStatuses`' value at mount time — a flag flip mid-session doesn't
 *  retroactively re-validate an already-parsed `?statuses=` param, same as
 *  every other URL-derived filter. */
export function useUrlFilters(
  validStatuses: ReadonlySet<CoverageCellStateWire>,
): UseUrlFiltersReturn {
  const location = useLocation();
  const history = useHistory();
  const [filters, setFiltersState] = useState<UrlFilters>(() =>
    readFiltersFromSearch(location.search, validStatuses),
  );

  const setFilters = useCallback(
    (updater: (prev: UrlFilters) => UrlFilters) => {
      setFiltersState((prev) => {
        const next = updater(prev);
        history.replace({
          ...history.location,
          search: writeFiltersToSearch(history.location.search, next),
        });
        return next;
      });
    },
    [history],
  );

  return { filters, setFilters };
}
